import { useEffect, useRef, useState } from 'react';
import { X, Loader2, Keyboard, ScanLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Camera barcode/QR scanner for the courier delivery flow.
 *
 * Strategy:
 *  - Use the native `BarcodeDetector` when available (Chrome / Android — the
 *    primary courier audience): cheap, fast, no extra bundle.
 *  - Fall back to `@zxing/browser` (lazy-loaded) for Safari / iOS where the
 *    native API is missing.
 *  - Always offer a manual-entry field (damaged label / unsupported browser).
 *
 * Calls `onDetect(value)` once on the first successful read, then stops.
 */
const FORMATS = [
  'code_128', 'ean_13', 'ean_8', 'code_39', 'itf',
  'upc_a', 'upc_e', 'qr_code', 'data_matrix', 'pdf417', 'aztec',
];

interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
interface ZxingControls {
  stop(): void;
}

export default function BarcodeScanner({
  onDetect,
  onClose,
}: {
  onDetect: (value: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const zxingControlsRef = useRef<ZxingControls | null>(null);
  const rafRef = useRef<number | null>(null);
  const doneRef = useRef(false);

  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [manual, setManual] = useState('');

  const finish = (value: string) => {
    if (doneRef.current) return;
    const v = value.trim();
    if (!v) return;
    doneRef.current = true;
    cleanup();
    onDetect(v);
  };

  const cleanup = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    try {
      zxingControlsRef.current?.stop();
    } catch {
      /* ignore */
    }
    zxingControlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const startNative = async (Detector: new (opts: { formats: string[] }) => NativeBarcodeDetector) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (cancelled) {
        stream.getTracks().forEach((tr) => tr.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play().catch(() => {});
      setStarting(false);

      let detector: NativeBarcodeDetector;
      try {
        detector = new Detector({ formats: FORMATS });
      } catch {
        detector = new Detector({ formats: ['qr_code', 'code_128', 'ean_13'] });
      }

      const tick = async () => {
        if (doneRef.current || cancelled) return;
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0 && codes[0].rawValue) {
            finish(codes[0].rawValue);
            return;
          }
        } catch {
          /* transient decode error — keep scanning */
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const startZxing = async () => {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      if (cancelled) return;
      const reader = new BrowserMultiFormatReader();
      const controls = await reader.decodeFromVideoDevice(
        undefined,
        videoRef.current!,
        (result) => {
          if (result) finish(result.getText());
        },
      );
      zxingControlsRef.current = controls as unknown as ZxingControls;
      setStarting(false);
    };

    const start = async () => {
      try {
        const Native = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => NativeBarcodeDetector })
          .BarcodeDetector;
        if (Native) {
          await startNative(Native);
        } else {
          await startZxing();
        }
      } catch (e) {
        if (cancelled) return;
        setStarting(false);
        const name = (e as { name?: string }).name;
        setError(
          name === 'NotAllowedError'
            ? t('parcels.scan.denied')
            : t('parcels.scan.unavailable'),
        );
      }
    };

    start();
    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Top bar: offset below the device status bar / notch in standalone PWA
          (env(safe-area-inset-top)) so the close button stays tappable. */}
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <span className="flex items-center gap-2 font-medium">
          <ScanLine className="w-5 h-5" /> {t('parcels.scan.title')}
        </span>
        <button onClick={() => { cleanup(); onClose(); }} className="p-1 rounded hover:bg-white/10">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video ref={videoRef} className="max-h-full max-w-full object-contain" playsInline muted />
        {starting && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        )}
        {!starting && !error && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-64 h-40 border-2 border-white/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-white/90">
            {error}
          </div>
        )}
      </div>

      {/* Manual fallback — always available. Offset above the home indicator /
          gesture bar (env(safe-area-inset-bottom)) so the input stays usable. */}
      <div
        className="p-4 bg-black/80"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <label className="block text-xs text-white/70 mb-1 flex items-center gap-1">
          <Keyboard className="w-3.5 h-3.5" /> {t('parcels.scan.manual')}
        </label>
        <div className="flex items-center gap-2">
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && finish(manual)}
            placeholder={t('parcels.scan.manual_ph')}
            className="flex-1 px-3 py-2 rounded-lg text-sm bg-white"
          />
          <button
            onClick={() => finish(manual)}
            disabled={!manual.trim()}
            className="px-4 py-2 bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {t('parcels.scan.validate')}
          </button>
        </div>
      </div>
    </div>
  );
}
